import { Add, Delete, Edit } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import {
  addLanguage,
  LanguageCells,
  deleteLanguage,
  editLanguage,
  getLanguages,
} from "framework/languages";
import { Feature } from "models/api";
import { ILanguage, ILanguageInput } from "models/language";
import { ICountry } from "models/country";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";
import AddLanguage from "./partials/AddLanguage/AddLanguage";
import EditLanguage from "./partials/EditLanguage/EditLanguage";
import DeleteLanguage from "./partials/DeleteLanguage/DeleteLanguage";
import Search from "components/Search/Search";
import { handleError } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { getPermission, getUserData } from "helpers";
import BlockIcon from "@mui/icons-material/Block";
import useFetchSection from "hooks/useFetchSection";

interface Props {
  feature: Feature;
}

const Languages: React.FC<Props> = ({ feature }) => {
  const { t, i18n } = useTranslation();
  const [loading2, setLoading2] = useState<boolean>(false);
  const [FilterLanguages, setFilterLanguages] = useState<ILanguage[]>([] as ILanguage[]);
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const user = getUserData();
  const { colors } = user;
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [selectedLanguage, setSelectedLanguage] = useState<ILanguage>({} as ILanguage);
  const [inputObj, setInputObj] = useState<ILanguageInput>({} as ILanguageInput);
  const { setAlert } = useAlert();

  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const addLanguagesMutation = useMutation({
    mutationFn: (createInput: ILanguageInput) => {
      return addLanguage(createInput);
    },
  });

  const editLanguagesMutation = useMutation({
    mutationFn: (data: { createInput: ILanguageInput; id: string }) => {
      return editLanguage(data.createInput, data.id);
    },
  });

  const deleteLanguagesMutation = useMutation({
    mutationFn: (id: string) => {
      return deleteLanguage(id);
    },
  });

  // const { loading:loadingFilters, filters, error:errorFilters, revalidate:revalidateFilters } = useFetchMultipleFilters({
  //   fetchFilters: {
  //     countries: getCountries,
  //     languages: getLanguages,
  //   },
  //   // processFilters: {
  //   //   countries: (data) => data.filter((country) => country.active),
  //   //   languages: (data) => data.filter((language) => language.active),
  //   // },
  //   onSuccess: (data) => {
  //     console.log("Filters fetched:", data);
  //   },
  // });

  // const { loading:loadingFilters, filters, error:errorFilters } = useFetchMultipleFilters({
  //   fetchFilters: {
  //     countries: getCountries,
  //     languages: getLanguages,
  //   },onSuccess: (data) => {
  //     console.log("Filters fetched:", data);
  //   }

  // });

  const {
    loading,
    data: Languages,
    error,
    permission,
    revalidate,
  } = useFetchSection({
    fetchData: () => getLanguages(),
    fetchPermission: () => getPermission(1),
    section: "Languages",
    onSuccess: (data) => setFilterLanguages(data),
    processResponse: (data) =>
      data.map((item: ILanguage) => {
        return { ...item, id: item.id };
      }),
  });

  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);

  const handleOpenEdit = (Language: ILanguage) => {
    setSelectedLanguage(Language);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedLanguage({} as ILanguage);
  };

  const handleOpenDelete = (Language: ILanguage) => {
    setSelectedLanguage(Language);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedLanguage({} as ILanguage);
  };

  const getBodyCells = (row: ILanguage) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "name")
        return {
          id: "name",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.name}</Typography>
            </TableCell>
          ),
        };

      if (key === "nameAr")
        return {
          id: "nameAr",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.nameAr}</Typography>
            </TableCell>
          ),
        };

      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                justifyContent={i18n.language === "en" ? "start" : "end"}
                spacing={1}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_edit")}>
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}
                {permission?.hasDeletePermission && (
                  <Tooltip title={t("T_delete")}>
                    <IconButton
                      size={"small"}
                      sx={{
                        color: colors.deleteIconColor
                          ? colors.deleteIconColor
                          : "red",
                        "&:hover": { color: colors?.deleteIconHoverColor },
                      }}
                      onClick={() => handleOpenDelete(row)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
          ),
        };
    });
    return cells;
  };

  const handleAdd = async () => {
    if (!inputObj.name || !inputObj.nameAr)
      return setAlert({
        open: true,
        message: "Please fill all fields",
        type: "error",
      });
    try {
      setLoading2(true);

      await addLanguagesMutation.mutateAsync(inputObj);
      setAlert({
        open: true,
        message: "Language added successfully",
        type: "success",
      });
      revalidate();
      handleCloseAdd();
      setInputObj({} as ILanguageInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {


    try {
      setLoading2(true);
      let createInput: ILanguageInput = {
        name: inputObj.name || selectedLanguage.name,
        nameAr: inputObj.nameAr || selectedLanguage.nameAr,
      };
      await editLanguagesMutation.mutateAsync({
        createInput,
        id: selectedLanguage.id,
      });
      setAlert({
        open: true,
        message: "Language updated successfully",
        type: "success",
      });
      revalidate();
      handleCloseEdit();
      setInputObj({} as ILanguageInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteLanguagesMutation.mutateAsync(selectedLanguage.id);
      setAlert({
        open: true,
        message: "Language deleted successfully",
        type: "success",
      });
      revalidate();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleSearch = async (value: string) => {
    if (!value) return setFilterLanguages(Languages!);
    let filtered = Languages!.filter((language) =>
      language.name.toLowerCase().includes(value.toLowerCase())
      || language.nameAr?.toLowerCase()?.includes(value.toLowerCase())
    );
    setFilterLanguages(filtered);
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          direction={"row"}
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Stack
            direction={"row"}
            spacing={1}
            justifyContent={"space-between"}
            width={"100%"}
          >
            <Typography
              variant="h3"
              color={colors?.labelColor ? colors?.labelColor : "header.primary"}
            >
              {t(feature.name)}
            </Typography>
            <Stack direction={"row"} spacing={1}>
              <Search handleChange={handleSearch} placeholder={t("T_languages")} />
              {permission?.hasModifyPermission && (
                <Tooltip title={t("T_add_language")}>
                  <IconButton
                    sx={{
                      color: colors.addUpdateIconColor
                        ? colors.addUpdateIconColor
                        : "red",
                      "&:hover": { color: colors?.addUpdateIconHoverColor },
                      height: 40,
                      width: 40,
                      transition: "0.5s",
                    }}
                    onClick={handleOpenAdd}
                  >
                    <Add />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Stack>

        </Grid>

        {permission?.hasReadPermission! && FilterLanguages.length > 0 && (
          <List
            headCells={LanguageCells}
            data={FilterLanguages}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        )}
        {(!FilterLanguages || FilterLanguages.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={t("T_no_language_header")}
              message={t("T_no_language_message")}
            />
          )}

        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>

      {openAdd && (
        <AddLanguage
          open={openAdd}
          loading={loading2}
          onClose={handleCloseAdd}
          onSubmit={handleAdd}
          onChanges={handleChange}
        />
      )}
      {openEdit && (
        <EditLanguage
          open={openEdit}
          loading={loading2}
          language={selectedLanguage}
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
          onChanges={handleChange}
        />
      )}
      {openDelete && (
        <DeleteLanguage
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}
    </>
  );
};

export default Languages;
